import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, Loader2, FileText, Lock, CheckCircle2, 
  Users, Megaphone, Rocket, Heart, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESSAGE_LIMIT = 10;

const CHANGE_STAGES = [
  { id: 'assess', name: 'Assess', icon: Users, description: "Who's affected" },
  { id: 'plan', name: 'Plan', icon: FileText, description: 'Bring people along' },
  { id: 'build', name: 'Build', icon: Megaphone, description: 'Create materials' },
  { id: 'implement', name: 'Implement', icon: Rocket, description: 'Support transition' },
  { id: 'embed', name: 'Embed', icon: Heart, description: 'Make it stick' },
];

export default function EmbedApp() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({ name: '', password: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [currentStage, setCurrentStage] = useState('assess');
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem('slp_embed_token');
    const savedSession = localStorage.getItem('slp_embed_session');
    
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      loadUserProject(savedToken);
    } else if (savedSession) {
      const session = JSON.parse(savedSession);
      setSessionId(session.id);
      setEmail(session.email);
      setMessageCount(session.messageCount || 0);
      setSessionStarted(true);
      setMessages(session.messages || [{
        id: 'welcome',
        role: 'assistant',
        content: "G'day! I'm your change coach. Tell me about your change initiative - what's happening?"
      }]);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadUserProject = async (authToken) => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.data.length > 0) {
        const project = response.data[0];
        setProjectId(project.id);
        const projResponse = await axios.get(`${API}/projects/${project.id}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setMessages(projResponse.data.messages || []);
        setSessionStarted(true);
      } else {
        // Create a new project
        const newProject = await axios.post(`${API}/projects`, {
          name: 'My Change Initiative',
          description: ''
        }, { headers: { Authorization: `Bearer ${authToken}` }});
        setProjectId(newProject.data.id);
        setSessionStarted(true);
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: "G'day! I'm your change coach. Tell me about your change initiative - what's happening?"
        }]);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    }
  };

  const startSession = async () => {
    if (!email.trim()) return;
    
    try {
      const response = await axios.post(`${API}/widget/session`, { email });
      setSessionId(response.data.id);
      setSessionStarted(true);
      
      const welcomeMsg = {
        id: 'welcome',
        role: 'assistant',
        content: "G'day! I'm your change coach. Tell me about your change initiative - what's happening?"
      };
      setMessages([welcomeMsg]);
      
      localStorage.setItem('slp_embed_session', JSON.stringify({
        id: response.data.id,
        email,
        messageCount: 0,
        messages: [welcomeMsg]
      }));
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error('Failed to start session');
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    if (!isAuthenticated && messageCount >= MESSAGE_LIMIT) {
      setShowSignup(true);
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputMessage
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setSending(true);

    try {
      let response;
      if (isAuthenticated && projectId) {
        response = await axios.post(`${API}/projects/${projectId}/chat`, {
          content: userMessage.content,
          project_id: projectId
        });
      } else {
        response = await axios.post(`${API}/widget/chat`, {
          session_id: sessionId,
          content: userMessage.content
        });
      }
      
      const assistantMsg = {
        id: response.data.id || `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.data.content
      };
      
      setMessages(prev => [...prev, assistantMsg]);
      
      const newCount = messageCount + 1;
      setMessageCount(newCount);
      
      // Save session
      if (!isAuthenticated) {
        const savedSession = JSON.parse(localStorage.getItem('slp_embed_session') || '{}');
        savedSession.messageCount = newCount;
        savedSession.messages = [...messages, userMessage, assistantMsg];
        localStorage.setItem('slp_embed_session', JSON.stringify(savedSession));
      }
      
      if (!isAuthenticated && newCount >= MESSAGE_LIMIT - 2) {
        setTimeout(() => setShowSignup(true), 1500);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSignup = async () => {
    if (!signupData.name.trim() || !signupData.password.trim()) return;
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password: signupData.password,
        name: signupData.name
      });
      
      const authToken = response.data.token;
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem('slp_embed_token', authToken);
      localStorage.removeItem('slp_embed_session');
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      // Create project
      const projectResponse = await axios.post(`${API}/projects`, {
        name: 'My Change Initiative',
        description: ''
      }, { headers: { Authorization: `Bearer ${authToken}` }});
      
      setProjectId(projectResponse.data.id);
      setShowSignup(false);
      toast.success('Account created! Your progress is saved.');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already registered')) {
        try {
          const loginResponse = await axios.post(`${API}/auth/login`, {
            email,
            password: signupData.password
          });
          const authToken = loginResponse.data.token;
          setToken(authToken);
          setIsAuthenticated(true);
          localStorage.setItem('slp_embed_token', authToken);
          setShowSignup(false);
          loadUserProject(authToken);
          toast.success('Welcome back!');
        } catch {
          toast.error('Account exists but password is incorrect');
        }
      } else {
        toast.error('Failed to create account');
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Email entry screen
  if (!sessionStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-slate-900" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">SLP Change Coach</h1>
            <p className="text-slate-400">
              Get practical guidance for your change initiative
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email to start"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mb-4"
              onKeyDown={(e) => e.key === 'Enter' && startSession()}
            />
            <Button 
              onClick={startSession}
              disabled={!email.trim()}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              Start Chatting
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
            
            <p className="text-xs text-slate-400 text-center mt-4">
              Free to try • No credit card required
            </p>
          </div>

          {/* Change journey preview */}
          <div className="mt-8">
            <p className="text-xs text-slate-500 text-center mb-4">THE CHANGE JOURNEY</p>
            <div className="flex justify-center gap-2">
              {CHANGE_STAGES.map((stage, idx) => {
                const Icon = stage.icon;
                return (
                  <div 
                    key={stage.id}
                    className="flex flex-col items-center"
                    title={stage.description}
                  >
                    <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                      <Icon className="h-5 w-5 text-amber-400" />
                    </div>
                    <span className="text-xs text-slate-400 mt-1">{stage.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main chat interface
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 text-white px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-slate-900" />
            </div>
            <div>
              <h1 className="font-semibold text-sm">SLP Change Coach</h1>
              <p className="text-xs text-slate-400">Your change management guide</p>
            </div>
          </div>
          
          {/* Stage indicator */}
          <div className="hidden sm:flex items-center gap-1">
            {CHANGE_STAGES.map((stage, idx) => {
              const Icon = stage.icon;
              const isActive = stage.id === currentStage;
              return (
                <div 
                  key={stage.id}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                    isActive ? 'bg-amber-500' : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  title={`${stage.name}: ${stage.description}`}
                  onClick={() => setCurrentStage(stage.id)}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-slate-900' : 'text-slate-300'}`} />
                </div>
              );
            })}
          </div>

          {!isAuthenticated && (
            <Button 
              size="sm" 
              variant="outline"
              className="text-white border-slate-600 hover:bg-slate-800"
              onClick={() => setShowSignup(true)}
            >
              Sign Up
            </Button>
          )}
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                  message.role === 'user'
                    ? 'bg-slate-900 text-white rounded-br-md'
                    : 'bg-white border border-slate-200 text-slate-700 rounded-bl-md shadow-sm'
                }`}
              >
                {message.content}
              </div>
            </motion.div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-md">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message limit warning */}
      {!isAuthenticated && messageCount >= MESSAGE_LIMIT - 3 && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-2">
          <p className="text-sm text-amber-700 text-center">
            {MESSAGE_LIMIT - messageCount} messages left • 
            <button onClick={() => setShowSignup(true)} className="underline ml-1 font-medium">
              Create free account for unlimited
            </button>
          </p>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-slate-200 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex gap-3">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Tell me about your change..."
              className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
              disabled={sending}
            />
            <Button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || sending}
              className="bg-slate-900 hover:bg-slate-800 px-6"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Quick actions for authenticated users */}
          {isAuthenticated && (
            <div className="flex justify-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUpgrade(true)}
                className="text-xs"
              >
                <FileText className="h-3 w-3 mr-1" />
                Generate Outputs
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Signup Modal */}
      <AnimatePresence>
        {showSignup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowSignup(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-slate-900 mb-2">Create Your Account</h2>
              <p className="text-slate-500 mb-6">Save your progress and unlock more features</p>
              
              <div className="space-y-3">
                <input
                  type="text"
                  value={signupData.name}
                  onChange={(e) => setSignupData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <input
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <Button
                  onClick={handleSignup}
                  disabled={!signupData.name.trim() || !signupData.password.trim()}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                >
                  Create Account
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-400 text-center">
                  Already have an account? Enter your email and password above to sign in.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgrade && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowUpgrade(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-slate-900 mb-2">Generate Change Outputs</h2>
              <p className="text-slate-500 mb-6">Turn your conversation into professional deliverables</p>
              
              <div className="space-y-3 mb-6">
                {[
                  { icon: FileText, name: 'Change Impact Assessment', desc: 'Who\'s affected and how' },
                  { icon: Megaphone, name: 'Communications Plan', desc: 'Key messages and approach' },
                  { icon: Users, name: 'Stakeholder Map', desc: 'Engagement strategy' },
                ].map((output, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <output.icon className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{output.name}</p>
                      <p className="text-xs text-slate-500">{output.desc}</p>
                    </div>
                    <Lock className="h-4 w-4 text-slate-300" />
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-800">
                  <strong>Upgrade to Professional</strong> to generate full change deliverables from your conversation.
                </p>
                <p className="text-xs text-amber-600 mt-1">$199/month or $1,990/year</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUpgrade(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900"
                  onClick={() => window.open(`${window.location.origin}/pricing`, '_blank')}
                >
                  View Plans
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
